/** @jsx jsx */

import { Fragment as _, useState, useRef, useEffect } from 'react';
import { css, jsx } from '@emotion/core';
import {
  DndContext,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from '@dnd-kit/core';
import Feedback from './Feedback';
import LayoutResponsiveContainer from './LayoutResponsiveContainer';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import { screenReaderInstructions, announcements } from './screenReader';

const Layout = ({
  id,
  columns,
  onDragStart,
  onDragEnd,
  feedback,
  useMediaInFeedback,
  setFeedback,
  reset,
}) => {
  const [allPlaceHoldersResolved, setAllPlaceHoldersResolved] = useState<
    boolean
  >(false);
  //

  const layout = useRef<HTMLDivElement | null>(null);
  const availableDropsIndex = useRef<number>(NaN);

  useEffect(() => {
    const onKeyDown = e => {
      if (['tab', 'esc'].includes(e.key)) {
        availableDropsIndex.current = NaN;
      }
    };

    addEventListener('keydown', onKeyDown);

    return () => {
      removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter(e, { currentCoordinates }) {
        const droppableContainers = columns.column_b.filter(cB => {
          return !cB?.props?.isDragDisabled;
        });

        if (droppableContainers.length === 0) {
          return;
        }

        const getXY = direction => {
          if (direction === 1) {
            if (isNaN(availableDropsIndex.current)) {
              availableDropsIndex.current = 0;
            } else {
              availableDropsIndex.current < droppableContainers.length - 1
                ? (availableDropsIndex.current += 1)
                : (availableDropsIndex.current = 0);
            }
          }

          if (direction === -1) {
            if (isNaN(availableDropsIndex.current)) {
              availableDropsIndex.current = droppableContainers.length - 1;
            } else {
              availableDropsIndex.current > 0
                ? (availableDropsIndex.current -= 1)
                : (availableDropsIndex.current =
                    droppableContainers.length - 1);
            }
          }

          let dropTarget = droppableContainers[availableDropsIndex.current];
          dropTarget = document.querySelector(
            `#match-drop-${dropTarget?.props?.id}`
          );

          if (!dropTarget) {
            return currentCoordinates;
          }

          return {
            x: dropTarget.getBoundingClientRect().left,
            y: dropTarget.getBoundingClientRect().top,
          };
        };

        switch (e.code) {
          case 'ArrowRight':
            return {
              ...getXY(1),
            };
          case 'ArrowLeft':
            return {
              ...getXY(-1),
            };
          case 'ArrowDown':
            return {
              ...getXY(1),
            };
          case 'ArrowUp':
            return {
              ...getXY(-1),
            };
        }

        return undefined;
      },
    })
  );

  const getPosition = id => {
    return {
      x: 0,
      y: 0,
    };
  };

  useEffect(() => {
    if (allPlaceHoldersResolved) {
      return;
    }

    const localAllPlaceHoldersResolved = columns.column_a.every((d, index) => {
      return d.key.includes('placeholder');
    });

    if (localAllPlaceHoldersResolved) {
      setAllPlaceHoldersResolved(true);
    }
  });

  return (
    <_>
      <div
        ref={layout}
        className={reset ? 'fader_-5s' : ''}
        css={css`
          position: relative;
          width: 100%;
        `}
        role="application"
      >
        <DndContext
          autoScroll={true}
          sensors={sensors}
          accessibility={{
            screenReaderInstructions,
            announcements,
          }}
          onDragStart={onDragStart}
          onDragCancel={() => {
            availableDropsIndex.current = NaN;
          }}
          onDragEnd={onDragEnd}
        >
          <LayoutResponsiveContainer>
            <div
              css={css`
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                width: 100%;

                ${allPlaceHoldersResolved &&
                  `
                  width: 0px;
                  height: 0px;
                  overflow: hidden;
                  transition: all 0.2s;
                `}
              `}
            >
              {columns.column_a}
            </div>
            <div
              css={css`
                display: flex;
                flex-wrap: wrap;
                justify-content: center;
                width: 100%;
              `}
            >
              {columns.column_b}
            </div>
          </LayoutResponsiveContainer>
        </DndContext>

        {feedback && (
          <Feedback
            useMediaInFeedback={useMediaInFeedback}
            onClose={() => {
              window.dev() && checkReturnToInteractionEditing(true);

              //
              let matchDragsFocusable: any = Array.from(
                //
                layout?.current?.querySelectorAll(
                  '.match-drag-focusable'
                ) as any
              );

              if (matchDragsFocusable.length) {
                matchDragsFocusable[0]?.focus();
              } else {
                (layout?.current
                  ?.closest('section')
                  ?.querySelector(
                    '[class*="navigationButton"]'
                  ) as HTMLAnchorElement)?.focus();
              }

              setFeedback(null);
            }}
          >
            {(feedback as any).props.children}
          </Feedback>
        )}
      </div>
    </_>
  );
};

export default Layout;
